import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useTheme } from "@mui/material/styles";
import {
  galleryInquiryCaptionSx,
  galleryInquiryLegalLinkSx,
  galleryInterestToastBodySx,
  galleryInterestToastCardSx,
  galleryInterestToastTitleSx,
  getGalleryInterestToastCloseButtonSx,
  getGalleryInterestToastWrapperSx,
} from "./galleryInquiryModalStyles";
import { MF_PRIVACY_URL, MF_TERMS_URL } from "../utils/legalLinks";

/**
 * Long enough to read two lines plus the fine print and still reach a legal
 * link. The durable record of what happened is the CTA's "Interest sent"
 * state, so this only has to carry the moment.
 */
const AUTO_HIDE_MS = 8000;

function InterestConsentText() {
  return (
    <Typography sx={{ ...galleryInquiryCaptionSx, display: "block", mt: 0.75 }}>
      They may email you about this residence. See our{" "}
      <Box
        component="a"
        href={MF_TERMS_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={galleryInquiryLegalLinkSx}
      >
        Terms
      </Box>{" "}
      and{" "}
      <Box
        component="a"
        href={MF_PRIVACY_URL}
        target="_blank"
        rel="noopener noreferrer"
        sx={galleryInquiryLegalLinkSx}
      >
        Privacy
      </Box>
      .
    </Typography>
  );
}

/**
 * Confirms a one-tap interest submission, and carries its failures — with no
 * modal mounted on that path, this is the only place either outcome can be
 * announced.
 */
export default function GalleryInterestToast({
  open,
  email,
  error = "",
  onClose,
  onRetry,
  bottom,
}) {
  const theme = useTheme();
  /** Auto-dismiss would pull the fine print away mid-read, or mid-tap. */
  const [held, setHeld] = useState(false);
  const failed = Boolean(error);

  useEffect(() => {
    if (!open || failed || held) return undefined;
    const timer = setTimeout(() => onClose?.(), AUTO_HIDE_MS);
    return () => clearTimeout(timer);
  }, [open, failed, held, onClose]);

  useEffect(() => {
    if (!open) setHeld(false);
  }, [open]);

  if (!open) return null;

  return (
    <Box sx={getGalleryInterestToastWrapperSx(bottom)}>
      <Box
        role={failed ? "alert" : "status"}
        aria-live={failed ? "assertive" : "polite"}
        onMouseEnter={() => setHeld(true)}
        onMouseLeave={() => setHeld(false)}
        onFocus={() => setHeld(true)}
        onBlur={() => setHeld(false)}
        sx={galleryInterestToastCardSx}
      >
        <IconButton
          aria-label="Dismiss"
          onClick={onClose}
          size="small"
          sx={getGalleryInterestToastCloseButtonSx(theme)}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>

        {failed ? (
          <>
            <Typography sx={galleryInterestToastTitleSx}>
              Couldn&apos;t share your interest
            </Typography>
            <Typography sx={{ ...galleryInterestToastBodySx, mt: 0.25 }}>
              {error}
            </Typography>
            {onRetry ? (
              <Button
                type="button"
                variant="text"
                size="small"
                onClick={onRetry}
                sx={{
                  mt: 0.5,
                  ml: -0.75,
                  fontSize: "13px",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Try again
              </Button>
            ) : null}
          </>
        ) : (
          <>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <CheckCircleOutlineIcon
                sx={{ fontSize: 17, color: theme.palette.success.main }}
              />
              <Typography sx={galleryInterestToastTitleSx}>
                Shared with the property team
              </Typography>
            </Box>
            {email ? (
              <Typography sx={{ ...galleryInterestToastBodySx, mt: 0.25 }}>
                They&apos;ll be in touch at {email}.
              </Typography>
            ) : null}
            <InterestConsentText />
          </>
        )}
      </Box>
    </Box>
  );
}

GalleryInterestToast.propTypes = {
  open: PropTypes.bool,
  email: PropTypes.string,
  error: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
  bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};
